import React, { useState } from "react"
import { motion } from "framer-motion"
import {Search, Eye, Trash2, FileText, Edit, Download, Home} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"
import toast from "react-hot-toast"
import VisualizarAforamento from "./VisualizarAforamento"

interface ListarAforamentsProps {
  onEdit?: (aforamentoId: string) => void
  onView?: (aforamentoId: string) => void
}

export default function ListarAforamentos({ onEdit, onView }: ListarAforamentsProps) {
  const { aforamentos, deleteAforamento, getAforamentoById } = useGuias()
  const [searchTerm, setSearchTerm] = useState("")
  const [viewingAforamento, setViewingAforamento] = useState<string | null>(null)

  const handleDelete = async (id: string, proprietario: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o aforamento de ${proprietario}?`)) {
      await deleteAforamento(id)
    }
  }

  const filteredAforamentos = aforamentos.filter(aforamento => 
    aforamento.proprietario.toLowerCase().includes(searchTerm.toLowerCase()) ||
    aforamento.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
    aforamento.denominacao.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-500 rounded-lg">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Aforamentos Cadastrados</h3>
              <p className="text-sm text-gray-500">Total: {aforamentos.length} documento(s)</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por proprietário, número ou denominação..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-96"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Número</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Proprietário</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Denominação</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Região</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ano</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredAforamentos.map((aforamento, index) => (
                <motion.tr
                  key={aforamento._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-4 text-sm text-gray-800 font-medium">{aforamento.numero}</td>
                  <td className="px-4 py-4 text-sm text-gray-800">{aforamento.proprietario}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{aforamento.denominacao}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{aforamento.regiao}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{aforamento.ano}</td>
                  <td className="px-4 py-4">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      {aforamento.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                        onClick={() => onEdit?.(aforamento._id!)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Editar aforamento"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => setViewingAforamento(aforamento._id!)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Visualizar aforamento"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(aforamento._id!, aforamento.proprietario)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir aforamento"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAforamentos.length === 0 && (
          <div className="text-center py-12">
            <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm 
                ? "Nenhum aforamento encontrado com esse filtro" 
                : "Nenhum aforamento cadastrado ainda"}
            </p>
          </div>
        )}
      </motion.div>

      {/* Modal de Visualização */}
      {viewingAforamento && (
        <VisualizarAforamento
          aforamento={getAforamentoById(viewingAforamento)!}
          onClose={() => setViewingAforamento(null)}
        />
      )}
    </div>
  )
}